import React, { useEffect, useState, useRef } from 'react';
import { fixHtmlImagePaths, processEmbedUrls } from "../utils/imageUtils";
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { normalizeImageUrl } from '../utils/imageUtils';
import { useData } from '../context/DataContext';
import { updateMetaTags } from '../utils/seoUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';
import ReviewSection from '../components/ReviewSection';

const PromotionDetailPage = () => {
    const { slug } = useParams();
    const { postCategories } = useData();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    const lastFetchedSlugRef = useRef(null);
    const isFetchingRef = useRef(false);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [post]);

    useEffect(() => {
        if (post) {
            updateMetaTags({
                title: post.title,
                description: post.description || post.title,
                image: post.imageUrl,
            });
        }
    }, [post]);

    useEffect(() => {
        const fetchPost = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;
            
            isFetchingRef.current = true;
            setLoading(true);
            try {
                const res = await api.get(`/api/posts/${slug}`);
                const data = res?.data || res;
                setPost(data);
                lastFetchedSlugRef.current = slug;

                const relRes = await api.get('/api/posts/promotions', { 
                    params: { size: 5, category: data.category } 
                });
                const relData = relRes?.data?.content || relRes?.data || [];
                const filtered = Array.isArray(relData)
                    ? relData.filter(p => (p._id || p.id) !== (data._id || data.id)).slice(0, 4)
                    : [];
                setRelatedPosts(filtered);
            } catch (err) {
                console.error('Error fetching promotion detail:', err);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };
        fetchPost();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) {
        return (
            <main style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <div className="spinner-border text-danger" role="status" />
                    <p className="mt-3 text-muted">Đang tải...</p>
                </div>
            </main>
        );
    }

    if (!post) {
        return (
            <main style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center">
                    <p className="text-muted">Không tìm thấy bài viết.</p>
                    <Link to="/khuyen-mai" style={{ color: '#ff0000', fontWeight: 700 }}>← Quay lại Khuyến Mại</Link>
                </div>
            </main>
        );
    }

    return (
        <main style={{ paddingBottom: 60 }}>
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li className="breadcrumb-item"><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item"><Link to="/khuyen-mai" className="hover:text-red-600 transition-colors no-underline text-slate-500">Khuyến Mại</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="breadcrumb-item active text-slate-900 font-bold" aria-current="page">{post.title}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <div className="container">
                <div className="row">
                    <div className="col-12 col-lg-9" style={{ minWidth: 0 }}>
                        {post.imageUrl && (
                            <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
                                <img
                                    src={normalizeImageUrl(post.imageUrl)}
                                    alt={post.title}
                                    style={{ width: '100%', maxHeight: 460, objectFit: 'cover' }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                            {post.category && (
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#ff0000', background: '#fff5ec', borderRadius: 999, padding: '4px 14px', border: '1px solid #ffe0c2' }}>
                                    {post.category}
                                </span>
                            )}
                            <span style={{ fontSize: 13, color: '#aaa' }}>
                                📅 {post.createdAt ? new Date(post.createdAt).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                            </span>
                        </div>

                        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#111', lineHeight: 1.4, marginBottom: 24, borderBottom: '2px solid #ff0000', paddingBottom: 16 }}>
                            {post.title}
                        </h1>

                        {post.description && (
                            <p style={{ fontSize: 16, color: '#444', lineHeight: 1.8, marginBottom: 28, fontStyle: 'italic', paddingLeft: 16, borderLeft: '4px solid #ff0000', background: '#fff8f2', borderRadius: '0 8px 8px 0', padding: '14px 18px' }}>
                                {post.description}
                            </p>
                        )}

                        {post.content && (
                            <div
                                ref={contentRef}
                                className="ql-content"
                                style={{ fontSize: 15.5, color: '#333', lineHeight: 1.9, wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(post.content?.replace(/&nbsp;/g, ' '))) }}
                            />
                        )}

                        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid #eee' }}>
                            <Link
                                to="/khuyen-mai"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#ff0000', fontWeight: 700, textDecoration: 'none', fontSize: 14 }}
                            >
                                ← Quay lại danh sách Khuyến Mại
                            </Link>
                        </div>

                        {/* Review & Rating Section */}
                        <ReviewSection
                            targetType="promotion"
                            targetId={slug || post?._id}
                            targetTitle={post?.title}
                        />
                    </div>

                    <div className="col-12 col-lg-3" style={{ minWidth: 0 }}>
                        <div style={{ position: 'sticky', top: 100 }}>
                            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111', borderLeft: '4px solid #ff0000', paddingLeft: 12, marginBottom: 20 }}>
                                Khuyến mại khác
                            </h3>

                            {relatedPosts.length === 0 ? (
                                <p style={{ fontSize: 14, color: '#aaa' }}>Đang cập nhật...</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                    {relatedPosts.map(related => (
                                        <Link
                                            key={related._id || related.id}
                                            to={`/khuyen-mai/${related.slug || related._id || related.id}`}
                                            style={{ textDecoration: 'none', display: 'flex', gap: 12, alignItems: 'flex-start' }}
                                        >
                                            <div style={{ flexShrink: 0, width: 80, height: 60, borderRadius: 8, overflow: 'hidden', background: '#f5f5f5' }}>
                                                {related.imageUrl ? (
                                                    <img
                                                        src={normalizeImageUrl(related.imageUrl)}
                                                        alt={related.title}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 22 }}>🎁</div>
                                                )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <p style={{ fontSize: 13, fontWeight: 700, color: '#222', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#ff0000'}
                                                    onMouseLeave={e => e.currentTarget.style.color = '#222'}
                                                >
                                                    {related.title}
                                                </p>
                                                <span style={{ fontSize: 11, color: '#aaa', marginTop: 4, display: 'block' }}>
                                                    {related.createdAt ? new Date(related.createdAt).toLocaleDateString('vi-VN') : ''}
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default PromotionDetailPage;
